<template>
  <div class="editor-container">
    <editor
      v-model="localContent"
      :init="editorInit"
      class="editor"
    />
  </div>
</template>

<script>
import "tinymce/tinymce";
import "tinymce/themes/silver";
import "tinymce/icons/default";
import "tinymce/skins/ui/oxide/skin.css";
import "tinymce/plugins/code";

import Editor from "@tinymce/tinymce-vue";

export default {
  components: { Editor },
  props: {
    tagSmartcom: { type: Boolean, default: true },
    tagNonave: { type: Boolean, default: true },
    tagNonpvl: { type: Boolean, default: true },
    tagCite: { type: Boolean, default: true },
    value: { type: String, default: "" },
    toolbar: {
      type: String,
      default:
        "| bold superscript underline | bullist numlist outdent indent | h6 | removeformat | link | table | searchreplace | copy pasteastext  cut  |  wordcount |  print | code | undo redo",
    },
  },
  data() {
    return {
      localContent: this.value,
      directTagableElements: [
        "H1",
        "H2",
        "H3",
        "H4",
        "H5",
        "H6",
        "TD",
        "TH",
        "TR",
        "TABLE",
        "THEAD",
        "TBODY",
        "UL",
        "OL",
        "LI",
        "A",
        "DIV",
        "BR",
        "P",
      ],
      editorInit: {
        menubar: false,
        content_css: "/editor.css",
        extended_valid_elements: "br[class]",
        toolbar: this.generateToolbar(),
        toolbar_mode: "wrap",
        language: "de",
        language_url: "/tinymce/langs/de.js",
        table_toolbar:
          "tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol",
        table_header_type: "cells",
        contextmenu: "link lists",
        plugins: this.generatePlugins(),
        setup: (editor) => {
          if (this.tagCite) this.addCiteTag(editor);
          if (this.tagSmartcom) this.addSmartcomTag(editor);
          if (this.tagNonave) this.addNonaveTag(editor);
          if (this.tagNonpvl) this.addNonpvlTag(editor);

          // Normalize: move class from td/th/tr to span(s)
          const normalizeCells = () => {
            const doc = editor.getDoc();
            ["smartcom", "nonave", "nonpvl"].forEach((cls) => {
              if (cls === "smartcom") return; // exclude smartcom from structural normalization
              // TD / TH
              doc.querySelectorAll(`td.${cls},th.${cls}`).forEach((cell) => {
                if (cell.classList.contains(cls)) {
                  if (
                    cell.childNodes.length === 1 &&
                    cell.firstChild.nodeName === "SPAN" &&
                    cell.firstChild.classList.contains(cls)
                  )
                    return;
                  const span = doc.createElement("span");
                  span.className = cls;
                  while (cell.firstChild) span.appendChild(cell.firstChild);
                  cell.appendChild(span);
                  cell.classList.remove(cls);
                }
              });
              // TR -> distribute to each cell content
              doc.querySelectorAll(`tr.${cls}`).forEach((row) => {
                row.classList.remove(cls);
                row.querySelectorAll("td,th").forEach((cell) => {
                  if (
                    !(
                      cell.childNodes.length === 1 &&
                      cell.firstChild.nodeName === "SPAN" &&
                      cell.firstChild.classList.contains(cls)
                    )
                  ) {
                    const span = doc.createElement("span");
                    span.className = cls;
                    while (cell.firstChild) span.appendChild(cell.firstChild);
                    cell.appendChild(span);
                  }
                });
              });
              // TABLE -> distribute to each cell like TR
              doc.querySelectorAll(`table.${cls}`).forEach((tbl) => {
                tbl.classList.remove(cls);
                tbl.querySelectorAll("td,th").forEach((cell) => {
                  if (
                    !(
                      cell.childNodes.length === 1 &&
                      cell.firstChild.nodeName === "SPAN" &&
                      cell.firstChild.classList.contains(cls)
                    )
                  ) {
                    const span = doc.createElement("span");
                    span.className = cls;
                    while (cell.firstChild) span.appendChild(cell.firstChild);
                    cell.appendChild(span);
                  }
                });
              });
              // TBODY -> distribute to its cells
              doc.querySelectorAll(`tbody.${cls}`).forEach((tb) => {
                tb.classList.remove(cls);
                tb.querySelectorAll("td,th").forEach((cell) => {
                  if (
                    !(
                      cell.childNodes.length === 1 &&
                      cell.firstChild.nodeName === "SPAN" &&
                      cell.firstChild.classList.contains(cls)
                    )
                  ) {
                    const span = doc.createElement("span");
                    span.className = cls;
                    while (cell.firstChild) span.appendChild(cell.firstChild);
                    cell.appendChild(span);
                  }
                });
              });
              // LI -> wrap entire content
              doc.querySelectorAll(`li.${cls}`).forEach((li) => {
                if (li.classList.contains(cls)) {
                  if (
                    li.childNodes.length === 1 &&
                    li.firstChild.nodeName === "SPAN" &&
                    li.firstChild.classList.contains(cls)
                  ) {
                    li.classList.remove(cls);
                    return;
                  }
                  const span = doc.createElement("span");
                  span.className = cls;
                  while (li.firstChild) span.appendChild(li.firstChild);
                  li.appendChild(span);
                  li.classList.remove(cls);
                }
              });
            });
          };

          // Ensure smartcom table and list class propagation
          const propagateSmartcomToTables = () => {
            const doc = editor.getDoc();
            // Tables
            doc.querySelectorAll("table").forEach((tbl) => {
              const anySmart = tbl.querySelector("td.smartcom,th.smartcom");
              if (anySmart) tbl.classList.add("smartcom");
              else tbl.classList.remove("smartcom");
              // Rows inside table
              tbl.querySelectorAll("tr").forEach((tr) => {
                const rowSmart = tr.querySelector("td.smartcom,th.smartcom");
                if (rowSmart) tr.classList.add("smartcom");
                else tr.classList.remove("smartcom");
              });
            });
            // Lists (ul/ol)
            doc.querySelectorAll("ul,ol").forEach((list) => {
              const anySmartLi = list.querySelector("li.smartcom");
              if (anySmartLi) list.classList.add("smartcom");
              else list.classList.remove("smartcom");
            });
            // Paragraphs
            doc.querySelectorAll("p").forEach((p) => {
              if (
                Array.from(p.childNodes).some((node) => node.nodeType === 1)
              ) {
                const onlySmartComNodes = Array.from(p.childNodes).every(
                  (node) => {
                    if (node.nodeType === 1)
                      return node.classList.contains("smartcom");
                    if (node.nodeType === 3) return false;
                    return false;
                  },
                );
                if (onlySmartComNodes) p.classList.add("smartcom");
              }
            });
          };

          // Propagate smartcom class to <br> elements following smartcom content and clean orphans
          const propagateSmartcomToBreaks = () => {
            const doc = editor.getDoc();
            // Remove orphan smartcom br (no smartcom context before)
            doc.querySelectorAll("br.smartcom").forEach((br) => {
              const hasContext = (() => {
                let prev = br.previousSibling;
                while (prev && prev.nodeType === 3 && !prev.nodeValue.trim())
                  prev = prev.previousSibling;
                if (prev && prev.nodeType === 1) {
                  if (
                    prev.classList.contains("smartcom") ||
                    prev.closest(".smartcom")
                  )
                    return true;
                  if (
                    prev.nodeName === "SPAN" &&
                    prev.classList.contains("smartcom")
                  )
                    return true;
                }
                return (
                  !!br.parentElement &&
                  (br.parentElement.classList.contains("smartcom") ||
                    !!br.parentElement.closest(".smartcom"))
                );
              })();
              if (!hasContext) br.classList.remove("smartcom");
            });
            // Add smartcom to br directly following smartcom content
            doc.querySelectorAll("br:not(.smartcom)").forEach((br) => {
              let prev = br.previousSibling;
              while (prev && prev.nodeType === 3 && !prev.nodeValue.trim())
                prev = prev.previousSibling;
              const prevIsSmart =
                prev &&
                prev.nodeType === 1 &&
                (prev.classList.contains("smartcom") ||
                  (prev.nodeName === "SPAN" &&
                    prev.classList.contains("smartcom")));
              const parentSmart =
                br.parentElement &&
                (br.parentElement.classList.contains("smartcom") ||
                  !!br.parentElement.closest(".smartcom"));
              if (prevIsSmart || parentSmart) br.classList.add("smartcom");
            });
          };

          const removeTempTags = () => {
            editor.dom.removeClass(editor.dom.select("*"), "tempSmartCom");
          };

          const refreshAll = () => {
            normalizeCells();
            propagateSmartcomToTables();
            propagateSmartcomToBreaks();
            removeTempTags();
            this.updateContent(editor.getContent());
          };

          editor.on("Change", refreshAll);
          editor.on("NodeChange", refreshAll);
          editor.on("init", function () {
            editor.formatter.register("cite", { inline: "cite" });
            refreshAll();
          });

          editor.ui.registry.addButton("pasteastext", {
            icon: "paste-text",
            tooltip: "Als reinen Text einfügen",
            onAction: function () {
              editor.pasteAsPlainText = true;
              navigator.clipboard.readText().then((clipText) => {
                editor.insertContent(`<p>${editor.dom.encode(clipText)}</p>`);
              });
            },
          });
        },
      },
    };
  },
  watch: {
    value(newContent) {
      this.localContent = newContent;
    },
    localContent(newContent, prev) {
      if (newContent !== prev) this.$emit("input", newContent);
    },
  },
  methods: {
    updateContent(content) {
      this.localContent = content;
    },
    generateToolbar() {
      let toolbar = this.toolbar;
      if (this.tagCite) toolbar = `customCiteButton ${toolbar}`;
      if (this.tagSmartcom) toolbar = `smartcom ${toolbar}`;
      if (this.tagNonave) toolbar = `nonave ${toolbar}`;
      if (this.tagNonpvl) toolbar = `nonpvl ${toolbar}`;
      return toolbar;
    },
    generatePlugins() {
      return "link code lists advlist searchreplace autolink print insertdatetime table autoresize";
    },
    addCiteTag(editor) {
      editor.ui.registry.addToggleButton("customCiteButton", {
        text: "Cite",
        onAction: () => editor.formatter.toggle("cite"),
        onSetup: (api) => {
          const h = () => api.setActive(editor.formatter.match("cite"));
          editor.on("NodeChange", h);
          return () => editor.off("NodeChange", h);
        },
      });
    },
    toggleBlockSpan(block, className) {
      if (
        block.childNodes.length === 1 &&
        block.firstChild.nodeName === "SPAN" &&
        block.firstChild.classList.contains(className)
      ) {
        const span = block.firstChild;
        while (span.firstChild) block.insertBefore(span.firstChild, span);
        span.remove();
      } else {
        Array.from(block.querySelectorAll(`span.${className}`)).forEach((s) => {
          while (s.firstChild) s.parentNode.insertBefore(s.firstChild, s);
          s.remove();
        });
        const span = document.createElement("span");
        span.className = className;
        while (block.firstChild) span.appendChild(block.firstChild);
        block.appendChild(span);
      }
      block.classList.remove(className);
    },
    addSmartcomTag(editor) {
      this.registerTag(editor, "smartcom", "SmartCom", "smartcom-button");
    },
    addNonaveTag(editor) {
      this.registerTag(editor, "nonave", "Non-AVE", "nonave-button");
    },
    addNonpvlTag(editor) {
      this.registerTag(editor, "nonpvl", "Non_PVL", "nonpvl-button");
    },
    registerTag(editor, key, label, css) {
      editor.ui.registry.addToggleButton(key, {
        text: label,
        onAction: () =>
          editor.undoManager.transact(() => this.tagText(editor, key)),
        onSetup: (api) => {
          setTimeout(() => {
            editor
              .getContainer()
              .querySelectorAll(".tox-tbtn")
              .forEach((b) => {
                if (b.innerText === label) b.classList.add(css);
              });
          }, 0);
          const h = () => {
            const node = editor.selection.getNode();
            api.setActive(!!editor.dom.getParent(node, `span.${key}`));
          };
          editor.on("NodeChange", h);
          return () => editor.off("NodeChange", h);
        },
      });
    },
    tagText(editor, tagName) {
      if (!editor.formatter.has(tagName))
        editor.formatter.register(tagName, {
          inline: "span",
          classes: tagName,
          toggle: true,
        });
      const sel = editor.selection;
      const rootNode = sel.getNode();
      const supported = this.findClosestSupportedNode(
        rootNode,
        this.directTagableElements,
      );
      const isComplex = this.isComplexSelection(sel);
      const hasTableCells =
        editor
          .getDoc()
          .querySelectorAll(
            'td[data-mce-selected="1"], th[data-mce-selected="1"]',
          ).length > 0;
      if (supported && !isComplex && !hasTableCells) {
        const parentText = supported.textContent;
        const selectedText = sel.getContent({ format: "text" });
        // Exclude smartcom from block wrapping logic
        const isWrapBlock =
          tagName !== "smartcom" && /^(H[1-6]|TD|TH)$/.test(supported.nodeName);
        if (!selectedText || selectedText === parentText) {
          if (isWrapBlock) {
            this.toggleBlockSpan(supported, tagName);
          } else {
            this.toggleClassOnElement(rootNode, tagName);
            if (
              tagName !== "smartcom" &&
              rootNode !== supported &&
              rootNode.classList &&
              rootNode.classList.contains(tagName)
            ) {
              sel.select(rootNode);
              editor.execCommand(
                "mceReplaceContent",
                false,
                rootNode.textContent,
              );
            }
          }
        } else {
          if (isWrapBlock) editor.formatter.toggle(tagName);
          else {
            if (supported.classList.contains(tagName) && tagName !== "smartcom")
              this.toggleClassOnElement(supported, tagName);
            if (
              tagName !== "smartcom" &&
              rootNode !== supported &&
              rootNode.classList &&
              rootNode.classList.contains(tagName)
            ) {
              const rt = rootNode.textContent;
              const s = sel.getSel();
              let a = s.anchorOffset,
                f = s.focusOffset;
              if (f < a) [a, f] = [f, a];
              sel.select(rootNode);
              editor.execCommand(
                "mceReplaceContent",
                false,
                `<span class="${tagName}">${rt.substring(0, a)}</span>${selectedText}<span class="${tagName}">${rt.substring(f)}</span>`,
              );
            } else {
              editor.formatter.toggle(tagName);
            }
          }
        }
      } else if (isComplex) this.applyComplexSelection(editor, tagName, sel);
      else if (hasTableCells) {
        if (tagName === "smartcom") {
          // allow direct class on selected cells for smartcom
          const cells = editor
            .getDoc()
            .querySelectorAll(
              'td[data-mce-selected="1"], th[data-mce-selected="1"]',
            );
          cells.forEach((c) => c.classList.toggle("smartcom"));
          editor.nodeChanged();
          this.updateContent(editor.getContent());
          return;
        }
        this.applyTableSelection(editor, tagName);
      } else editor.formatter.toggle(tagName);
      editor.nodeChanged();
      this.updateContent(editor.getContent());
    },
    isComplexSelection(selection) {
      const html = selection.getContent({ format: "html" });
      return html.includes("<") && selection.getSelectedBlocks().length > 1;
    },
    applyComplexSelection(editor, tagName, selection) {
      selection.getSelectedBlocks().forEach((b) => {
        if (this.directTagableElements.includes(b.nodeName))
          this.toggleClassOnElement(b, tagName);
        else {
          const r = document.createRange();
          r.selectNodeContents(b);
          selection.setRng(r);
          editor.formatter.toggle(tagName);
        }
      });
      selection.setRng(selection.getRng());
    },
    findClosestSupportedNode(node, supported) {
      while (node && node.nodeName !== "BODY") {
        if (supported.includes(node.nodeName)) return node;
        node = node.parentNode;
      }
      return null;
    },
    toggleClassOnElement(node, className) {
      if (className === "smartcom") {
        // allow direct class everywhere except avoid duplicating
        node.classList.toggle(className);
        return;
      }
      // Prevent applying class directly to headings / table structure / rows / cells / list items / table / tbody itself; wrap or distribute.
      if (/^(H[1-6]|TD|TH)$/.test(node.nodeName)) {
        this.toggleBlockSpan(node, className);
        return;
      }
      if (node.nodeName === "TR") return; // never apply directly to row
      if (node.nodeName === "TABLE" || node.nodeName === "TBODY") {
        node.querySelectorAll("td,th").forEach((cell) => {
          this.toggleBlockSpan(cell, className);
        });
        return;
      }
      if (node.nodeName === "LI") {
        this.toggleBlockSpan(node, className);
        return;
      }
      if (node.classList.contains(className)) node.classList.remove(className);
      else node.classList.add(className);
    },
    selectionContainsTableCells(selection) {
      return selection
        .getSelectedBlocks()
        .some((b) => b.nodeName === "TD" || b.nodeName === "TH");
    },
    applyTableSelection(editor, tagName) {
      const cells = editor
        .getDoc()
        .querySelectorAll(
          'td[data-mce-selected="1"], th[data-mce-selected="1"]',
        );
      cells.forEach((cell) => {
        if (
          cell.childNodes.length === 1 &&
          cell.firstChild.nodeName === "SPAN" &&
          cell.firstChild.classList.contains(tagName)
        ) {
          const span = cell.firstChild;
          while (span.firstChild) cell.insertBefore(span.firstChild, span);
          span.remove();
        } else {
          Array.from(cell.querySelectorAll(`span.${tagName}`)).forEach((s) => {
            while (s.firstChild) s.parentNode.insertBefore(s.firstChild, s);
            s.remove();
          });
          const w = editor.getDoc().createElement("span");
          w.className = tagName;
          while (cell.firstChild) w.appendChild(cell.firstChild);
          cell.appendChild(w);
        }
        cell.classList.remove(tagName);
      });
      editor.nodeChanged();
      this.updateContent(editor.getContent());
    },
  },
};
</script>

<style>
.editor-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.editor {
  flex-grow: 1;
}
.tox-tbtn__select-label {
  cursor: pointer !important;
}
.smartcom-button {
  background-color: rgba(255, 0, 14, 0.327) !important;
  padding: 0 4px !important;
  border-radius: 4px !important;
  margin-right: 4px !important;
  transition: background-color 0.3s ease !important;
  cursor: pointer !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}
.smartcom-button:hover {
  background-color: rgba(255, 0, 14, 0.5) !important;
}
.tox-tbtn.smartcom-button:active,
.tox-tbtn.smartcom-button.tox-tbtn--select.tox-tbtn--enabled {
  background-color: rgba(204, 0, 11, 0.7) !important;
}
.tox-tbtn.smartcom-button.tox-tbtn--enabled.tox-tbtn--active:hover {
  background-color: rgba(170, 0, 9, 0.7) !important;
}
.nonave-button {
  background-color: rgba(41, 205, 255, 0.432) !important;
  padding: 0 4px !important;
  border-radius: 4px !important;
  margin-right: 4px !important;
  transition: background-color 0.3s ease !important;
  cursor: pointer !important;
}
.nonave-button:hover {
  background-color: rgba(41, 205, 255, 0.6) !important;
}
.tox-tbtn.nonave-button:active,
.tox-tbtn.nonave-button.tox-tbtn--select.tox-tbtn--enabled {
  background-color: rgba(0, 123, 184, 0.7) !important;
}
.nonpvl-button {
  background-color: rgba(248, 248, 0, 0.377) !important;
  padding: 0 4px !important;
  border-radius: 4px !important;
  margin-right: 4px !important;
  transition: background-color 0.3s ease !important;
  cursor: pointer !important;
}
.nonpvl-button:hover {
  background-color: rgba(248, 248, 0, 0.6) !important;
}
.tox-tbtn.nonpvl-button:active,
.tox-tbtn.nonpvl-button.tox-tbtn--select.tox-tbtn--enabled {
  background-color: rgba(204, 204, 0, 0.7) !important;
}
</style>
